import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAuthStore } from '@stores/AuthStore.js'

/**
 * The subscriber's plan/billing state (SUB-MVP-21). Loads status from finder,
 * starts a hosted Stripe Checkout (redirecting the browser to it), and opens the
 * Stripe Billing Portal to manage/cancel. Not persisted. Mirrors ProfileStore's
 * Bearer auth + error handling.
 */
export const useSubscriptionStore = defineStore('subscriptionStore', () => {
  const authStore = useAuthStore()

  const isSubscriber = ref(false)
  const subscription = ref(null)
  const offer = ref(null)
  const loading = ref(false)
  const loaded = ref(false) // a first successful load has happened

  // True only once we know the client is not a subscriber: drives the member-area
  // write gating + the subscribe notice. Unknown (pre-load) never locks the UI.
  const mustSubscribe = computed(() => loaded.value && !isSubscriber.value)
  const loadError = ref(false)
  const acting = ref(false) // a checkout/portal redirect is being prepared
  const actionError = ref(null)
  const preview = ref(null) // proration preview for a pending plan change
  const previewing = ref(false)
  const previewError = ref(false)

  const headers = () => ({ Authorization: `Bearer ${authStore.token}` })

  const handleAuthError = (error) => {
    if ([401, 403].includes(error?.response?.status)) {
      authStore.clearSession()
      if (!import.meta.env.SSR) window.location.href = '/'
      return true
    }
    return false
  }

  // Stale-while-revalidate: only show the blocking spinner on the first load.
  // A revisit keeps the cached data on screen and refreshes silently.
  const load = async () => {
    const spin = !loaded.value
    if (spin) loading.value = true
    loadError.value = false
    try {
      const { data } = await axios.get('/api/client/subscription', { headers: headers() })
      isSubscriber.value = !!data.is_subscriber
      subscription.value = data.subscription ?? null
      offer.value = data.offer ?? null
      loaded.value = true
    } catch (error) {
      if (!handleAuthError(error)) loadError.value = true
    } finally {
      if (spin) loading.value = false
    }
  }

  const origin = () => (import.meta.env.SSR ? '' : window.location.origin)

  // Start hosted Stripe Checkout and hand the browser to it. The success/cancel
  // URLs come back to this page, which reads the ?checkout flag. `plan` selects
  // the billing option (monthly/semestral/annual); omitted falls back to the
  // backend's default price, so older callers keep working.
  const subscribe = async (plan = null) => {
    acting.value = true
    actionError.value = null
    try {
      const { data } = await axios.post(
        '/api/client/subscription/checkout',
        {
          ...(plan ? { plan } : {}),
          success_url: `${origin()}/subscription?checkout=success`,
          cancel_url: `${origin()}/subscription?checkout=cancelled`,
        },
        { headers: headers() }
      )
      window.location.href = data.url
      return true
    } catch (error) {
      if (handleAuthError(error)) return false
      if (error?.response?.status === 409) {
        // Already subscribed elsewhere — re-sync instead of erroring.
        await load()
      } else {
        actionError.value = 'We could not start checkout. Please try again.'
      }
      acting.value = false
      return false
    }
    // No finally: on success the page is navigating away to Stripe.
  }

  const manageBilling = async () => {
    acting.value = true
    actionError.value = null
    try {
      const { data } = await axios.post(
        '/api/client/subscription/portal',
        { return_url: `${origin()}/subscription` },
        { headers: headers() }
      )
      window.location.href = data.url
      return true
    } catch (error) {
      if (handleAuthError(error)) return false
      if (error?.response?.status === 409) {
        await load()
      } else {
        actionError.value = 'We could not open the billing portal. Please try again.'
      }
      acting.value = false
      return false
    }
  }

  // Preview what switching to a plan costs now (a prorated charge for an
  // upgrade, or a credit for a downgrade), so the UI can explain the billing.
  const previewPlan = async (plan) => {
    preview.value = null
    previewError.value = false
    previewing.value = true
    try {
      const { data } = await axios.get('/api/client/subscription/change-plan/preview', {
        params: { plan },
        headers: headers(),
      })
      preview.value = data
    } catch (error) {
      if (! handleAuthError(error)) previewError.value = true
    } finally {
      previewing.value = false
    }
  }

  // Switch the active subscription to another billing option. The backend
  // updates Stripe with proration; we reload so the account shows the new plan.
  const changePlan = async (plan) => {
    acting.value = true
    actionError.value = null
    try {
      await axios.post('/api/client/subscription/change-plan', { plan }, { headers: headers() })
      await load()
      return true
    } catch (error) {
      if (handleAuthError(error)) return false
      actionError.value = 'We could not change your plan. Please try again.'
      return false
    } finally {
      acting.value = false
    }
  }

  const reset = () => {
    isSubscriber.value = false
    subscription.value = null
    offer.value = null
    loading.value = false
    loaded.value = false
    loadError.value = false
    acting.value = false
    actionError.value = null
  }

  return {
    isSubscriber,
    mustSubscribe,
    subscription,
    offer,
    loading,
    loadError,
    acting,
    actionError,
    load,
    subscribe,
    manageBilling,
    changePlan,
    preview,
    previewing,
    previewError,
    previewPlan,
    reset,
  }
})
